// src/pages/admin/Procedures.jsx — Com painel lateral de edição
import React, { useState } from 'react';
import { useApp } from 'contexts/AppContext';
import { proceduresService } from 'infrastructure/firebase';
import { Button, Alert, Avatar, FilterChip, EmptyState, TextArea, ChipMultiSelect, PhotoUpload } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';


const fmtBRL = v => (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits:2, maximumFractionDigits:2 });

// ── Procedure Card no Grid ──────────────────────────────────────
function ProcedureListCard({ procedure, professionals, onEdit }) {
  const proIds = (procedure.professionals?.length > 0) ? procedure.professionals : (procedure.professionalId ? [procedure.professionalId] : []);
  const pros = proIds.map(id => professionals.find(p => p.id === id)).filter(Boolean);
  
  return (
    <div style={{
      background:'#fff', border:'1px solid #E8E5EC', borderRadius:20, overflow:'hidden',
      display:'flex', flexDirection:'column', fontFamily:"'DM Sans',sans-serif",
    }}>
      <div style={{
        height:96, 
        background: procedure.photo ? `url(${procedure.photo}) center/cover` : 'linear-gradient(135deg,#B5A2D2,#C9929A)',
        display:'flex', alignItems:'center', justifyContent:'center', fontSize:38, color:'#fff',
        position:'relative',
      }}>
        {!procedure.photo && (procedure.emoji || '💆')}
        {!procedure.approved && (
          <span style={{ position:'absolute', top:10, right:10, background:'#F5E4D2',
            color:'#80551F', fontSize:10, fontWeight:600, padding:'3px 8px', borderRadius:20 }}>
            ⏳ Pendente
          </span>
        )}
      </div>
      <div style={{ padding:'14px 16px 16px', display:'flex', flexDirection:'column', gap:6, flex:1 }}>
        <div style={{ fontWeight:600, fontSize:14, color:'#3D3447', lineHeight:1.3 }}>{procedure.name}</div>
        <div style={{ fontSize:11, color:'#A8A1B0' }}>
          {procedure.duration}min · <span style={{ color:'#6B5876', fontWeight:600 }}>R$ {fmtBRL(procedure.price)}</span>
        </div>
        {procedure.description && (
          <p style={{ fontSize:12, color:'#6B6070', lineHeight:1.5, margin:'6px 0 8px',
            display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
            {procedure.description}
          </p>
        )}
        {pros.length > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:10 }}>
            <div style={{ display:'flex' }}>
              {pros.slice(0,3).map((p, i) => (
                <div key={p.id} style={{ marginLeft: i ? -8 : 0, boxShadow:'0 0 0 2px #fff', borderRadius:'50%' }}>
                  <Avatar name={p.name} color={p.avatarColor} size="sm" />
                </div>
              ))}
            </div>
            <span style={{ fontSize:11, color:'#A8A1B0', marginLeft:4 }}>
              {pros.length} {pros.length === 1 ? 'profissional' : 'profissionais'}
            </span>
          </div>
        )}
        <Button variant="secondary" size="sm" onClick={() => onEdit?.(procedure)} style={{ marginTop:'auto' }}>
          Editar
        </Button>
      </div>
    </div>
  );
}

// ── Painel Lateral de Edição ────────────────────────────────────
function ProcedureFormPanel({ procedure, professionals, procedures, onClose, onSave, showToast }) {
  const isMobile = useMobile();
  const isNew = !procedure || !procedure.id;
  const [form, setForm] = useState(() => ({
    name: procedure?.name || '',
    duration: procedure?.duration || 60,
    price: procedure?.price || '',
    photo: procedure?.photo || null,
    emoji: procedure?.emoji || '💆',
    description: procedure?.description || '',
    category: procedure?.category || 'Faciais',
    professionals: (procedure?.professionals?.length > 0) ? procedure.professionals : (procedure?.professionalId ? [procedure.professionalId] : []),
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.price || form.professionals.length === 0) {
      showToast('Preencha nome, valor e selecione ao menos um profissional.', 'warning');
      return;
    }
    // Verificar duplicata pelo nome (case-insensitive)
    if (isNew) {
      const nameLower = form.name.toLowerCase().trim();
      const already   = (procedures || []).some(p =>
        p.name.toLowerCase().trim() === nameLower && p.id !== procedure?.id
      );
      if (already) {
        showToast(`Já existe um procedimento com o nome "${form.name}". Use outro nome ou edite o existente.`, 'danger');
        return;
      }
    }
    setSaving(true);
    try {
      const data = {
        name: form.name.trim(),
        duration: parseInt(form.duration),
        price: parseFloat(form.price.toString().replace(',', '.')),
        category: form.category,
        emoji: form.emoji,
        photo: form.photo,
        description: form.description,
        professionals: form.professionals,
        approved: true,
      };
      
      if (isNew) {
        await proceduresService.create(data);
        showToast(`"${form.name}" foi cadastrado`);
      } else {
        await proceduresService.update(procedure.id, data);
        showToast(`"${form.name}" foi atualizado`);
      }
      onClose();
    } catch (e) {
      showToast('Erro ao salvar procedimento.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Excluir "${procedure.name}"?`)) return;
    try {
      await proceduresService.delete(procedure.id);
      showToast(`"${procedure.name}" foi excluído`);
      onClose();
    } catch {
      showToast('Erro ao excluir.', 'danger');
    }
  };

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(61,52,71,0.32)',
        backdropFilter:'blur(6px)', zIndex:150 }} />
      
      {/* Side Panel */}
      <div style={{
        position:'fixed', top:0, right:0, bottom:0, width:'min(540px, 100vw)', background:'#fff',
        zIndex:151, boxShadow:'-12px 0 40px rgba(74,63,88,0.18)', display:'flex', flexDirection:'column',
        fontFamily:"'DM Sans',sans-serif",
      }}>
        {/* Header */}
        <div style={{ padding:'22px 28px', borderBottom:'1px solid #E8E5EC',
          display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:24, color:'#3D3447' }}>
              {isNew ? 'Novo procedimento' : 'Editar procedimento'}
            </div>
            <div style={{ fontSize:12, color:'#A8A1B0', marginTop:2 }}>
              {isNew ? 'Defina os detalhes que aparecerão para profissionais e clientes' : `Editando "${procedure.name}"`}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'#F0EDF3', border:'none', cursor:'pointer',
            width:32, height:32, borderRadius:8, color:'#6B6070', fontSize:18 }}>×</button>
        </div>

        {/* Body — scroll */}
        <div style={{ padding:'24px 28px', flex:1, overflowY:'auto' }}>
          <div style={{ fontSize:10, fontWeight:600, color:'#A8A1B0', textTransform:'uppercase',
            letterSpacing:1, marginBottom:14 }}>
            Para agenda & cobrança
          </div>

          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
              Nome do procedimento <span style={{ color:'#C9929A' }}>*</span>
            </label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex.: Limpeza de Pele"
              style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
          </div>

          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:14, marginBottom:18 }}>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
                Tempo médio <span style={{ color:'#C9929A' }}>*</span>
              </label>
              <input value={form.duration} onChange={e => set('duration', e.target.value.replace(/\D/g,''))} placeholder="60"
                style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
              <div style={{ fontSize:11, color:'#A8A1B0', marginTop:4 }}>Em minutos. Determina a duração dos slots na agenda.</div>
            </div>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
                Valor <span style={{ color:'#C9929A' }}>*</span>
              </label>
              <input value={form.price} onChange={e => set('price', e.target.value)} placeholder="0,00"
                style={{ width:'100%', padding:'12px 14px', border:'1.5px solid #E8E5EC', borderRadius:12, fontSize:14, outline:'none', boxSizing:'border-box', color:'#3D3447', background:'#fff' }} />
            </div>
          </div>

          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
              Profissionais que realizam <span style={{ color:'#C9929A' }}>*</span>
            </label>
            <div style={{ fontSize:11, color:'#A8A1B0', marginBottom:8 }}>
              Apenas os marcados poderão ser escolhidos no agendamento deste procedimento.
            </div>
            <ChipMultiSelect
              options={professionals.map(p => ({ id:p.id, label:p.name, color:p.avatarColor, spec:p.specialty }))}
              value={form.professionals}
              onChange={v => set('professionals', v)}
              renderOption={(o, active) => (
                <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                  <Avatar name={o.label} color={o.color} size="sm" />
                  {o.label}
                </span>
              )}
            />
          </div>

          <hr style={{ border:'none', borderTop:'1px solid #E8E5EC', margin:'24px 0 18px' }} />

          <div style={{ fontSize:10, fontWeight:600, color:'#A8A1B0', textTransform:'uppercase',
            letterSpacing:1, marginBottom:14 }}>
            Apresentação ao cliente
          </div>

          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:'#6B6070', marginBottom:6 }}>
              Foto do procedimento
            </label>
            <div style={{ fontSize:11, color:'#A8A1B0', marginBottom:8 }}>
              Aparece no card que o cliente vê ao escolher.
            </div>
            <PhotoUpload
              shape="rounded"
              value={form.photo}
              emoji={form.emoji}
              onChange={v => set('photo', v)}
            />
          </div>

          <TextArea 
            label="Sobre o procedimento" 
            hint="Resumo curto exibido ao cliente." 
            value={form.description} 
            onChange={v => set('description', v)} 
            maxLength={220}
            placeholder="Ex.: Higienização profunda do rosto com extração e máscara calmante. Ideal para pele oleosa." 
          />
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 28px', borderTop:'1px solid #E8E5EC', background:'#FAF7FB',
          display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0, gap:12 }}>
          {!isNew ? (
            <Button variant="ghost" size="sm" onClick={handleDelete} style={{ color:'#7A3C44' }}>
              🗑 Excluir
            </Button>
          ) : <span />}
          <div style={{ display:'flex', gap:10 }}>
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button variant="primary" disabled={saving} loading={saving} onClick={handleSave}>
              {isNew ? 'Cadastrar procedimento' : 'Salvar alterações'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Main View ────────────────────────────────────────────────────
export default function AdminProcedures() {
  const { procedures, professionals, showToast } = useApp();
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [linking, setLinking] = useState(false);

  // Procedimentos sem nenhum profissional vinculado (checa ambos os campos)
  const unlinked = procedures.filter(p => {
    const ids = (p.professionals?.length > 0) ? p.professionals : (p.professionalId ? [p.professionalId] : []);
    return ids.length === 0;
  });

  const handleLinkAll = async () => {
    if (professionals.length === 0) {
      showToast('Cadastre ao menos um profissional antes de vincular.', 'warning');
      return;
    }
    if (!window.confirm(`Vincular ${professionals.length} profissional(is) a todos os ${unlinked.length} procedimento(s) sem vínculo?`)) return;
    setLinking(true);
    try {
      const proIds = professionals.map(p => p.id);
      for (const proc of unlinked) {
        await proceduresService.update(proc.id, { professionals: proIds, approved: true });
      }
      showToast(`✅ ${unlinked.length} procedimento(s) vinculados a ${professionals.length} profissional(is)!`);
    } catch {
      showToast('Erro ao vincular profissionais.', 'danger');
    } finally {
      setLinking(false);
    }
  };

  const pending = procedures.filter(p => !p.approved);
  const approved = procedures.filter(p => p.approved);
  const list = tab === 'all' ? procedures : tab === 'approved' ? approved : pending;
  
  const filtered = list.filter(p => !query || p.name?.toLowerCase().includes(query.toLowerCase()));

  const handleApprove = async (id) => {
    try { 
      await proceduresService.approve(id); 
      showToast('Procedimento aprovado!'); 
    } catch { 
      showToast('Erro ao aprovar.','danger'); 
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Recusar e remover este procedimento?')) return;
    try { 
      await proceduresService.reject(id); 
      showToast('Procedimento removido.'); 
    } catch { 
      showToast('Erro.','danger'); 
    }
  };

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>
      {/* Alerta: procedimentos sem profissionais vinculados */}
      {unlinked.length > 0 && professionals.length > 0 && (
        <div style={{
          background:'#fffbeb', border:'1px solid #fcd34d', borderRadius:12,
          padding:'14px 18px', marginBottom:20, display:'flex',
          alignItems:'center', justifyContent:'space-between', gap:16, flexWrap:'wrap',
        }}>
          <div style={{ fontSize:13, color:'#78350f' }}>
            ⚠️ <strong>{unlinked.length} procedimento(s)</strong> sem profissionais vinculados — clientes não conseguem agendar esses serviços.
          </div>
          <Button variant="warning" size="sm" loading={linking} disabled={linking} onClick={handleLinkAll}>
            🔗 Vincular todos os profissionais
          </Button>
        </div>
      )}

      {/* Pending alert */}
      {pending.length > 0 && (
        <Alert type="warning" title={`${pending.length} procedimento(s) aguardando aprovação`}>
          {pending.map(p => {
            const pro = professionals.find(pr=>pr.id===p.professionalId);
            return (
              <div key={p.id} style={{ display:'flex',alignItems:'center',gap:12,marginTop:10,flexWrap:'wrap' }}>
                <span style={{ fontSize:20 }}>{p.emoji}</span>
                <span><strong>{p.name}</strong> — {pro?.name}</span>
                <span style={{ color:'#A8A1B0',fontSize:12 }}>R$ {p.price} • {p.duration}min</span>
                <Button variant="success" size="sm" onClick={()=>handleApprove(p.id)}>✓ Aprovar</Button>
                <Button variant="danger" size="sm" onClick={()=>handleReject(p.id)}>✗ Recusar</Button>
              </div>
            );
          })}
        </Alert>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:32, color:'#3D3447' }}>Procedimentos</div>
          <div style={{ fontSize:13, color:'#A8A1B0', marginTop:2 }}>
            {procedures.length} cadastrados · {pending.length} aguardando aprovação
          </div>
        </div>
        <Button variant="primary" onClick={() => setEditing({})}>+ Novo procedimento</Button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:1, minWidth:240, maxWidth:380 }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#A8A1B0', fontSize:14 }}>🔍</span>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar procedimento..."
            style={{ width:'100%', padding:'9px 14px 9px 36px', border:'1.5px solid #E8E5EC',
              borderRadius:12, fontSize:14, outline:'none', boxSizing:'border-box',
              color:'#3D3447', background:'#fff' }} />
        </div>
        <FilterChip label={`Todos (${procedures.length})`} active={tab==='all'} onClick={() => setTab('all')} />
        <FilterChip label={`Aprovados (${approved.length})`} active={tab==='approved'} onClick={() => setTab('approved')} />
        <FilterChip label={`Pendentes (${pending.length})`} active={tab==='pending'} onClick={() => setTab('pending')} />
      </div>

      {/* Grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:16 }}>
        {filtered.map(p => (
          <ProcedureListCard key={p.id} procedure={p} professionals={professionals} onEdit={setEditing} />
        ))}
      </div>

      {filtered.length === 0 && (
        <EmptyState 
          icon="💆" 
          title="Nenhum procedimento encontrado" 
          text="Tente outro filtro ou cadastre um novo." 
        />
      )}

      {/* Side panel */}
      {editing && (
        <ProcedureFormPanel 
          procedure={editing} 
          professionals={professionals}
          procedures={procedures}
          showToast={showToast} 
          onClose={() => setEditing(null)} 
        />
      )}
    </div>
  );
}
